import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, map } from 'rxjs';
import swal from 'sweetalert2';
import { AppSharedModule } from 'app/app-shared.module';
import { AuthStateService } from 'app/core/services/auth-state.service';
import {
  formatLocalizedNumber,
  getDecimalSeparator,
} from 'app/modules/utilities/shared/localized-number';
import {
  advanceToNextPass,
  calculatePassToPass,
  canAdvanceToNextPass,
  createEmptyPassToPassDraft,
  DraftInputField,
  FIRST_PASS_NUMBER,
  normalizeDraftField,
  OFFSET_DIGITS,
  PassToPassDraft,
  revertToPreviousPass,
  TurnDirection,
  WIDTH_DIGITS,
} from './calculator';
import { PassToPassStorageService } from './pass-to-pass-storage.service';
import { parseDecimal } from 'app/modules/utilities/shared/decimal-input';

type PassAction = 'nextPass' | 'previousPass' | 'restart';

interface DiagramMeasurement {
  label: string;
  value: string;
}

const I18N_PREFIX = 'pass-to-pass-calculator';

/** Label of the confirm button shown in each action's confirmation dialog. */
const CONFIRM_BUTTON_KEYS: Record<PassAction, string> = {
  nextPass: 'confirmNextPass',
  previousPass: 'confirmPreviousPass',
  restart: 'restart',
};

const TURN_LABEL_KEYS: Record<TurnDirection, string> = {
  left: `${I18N_PREFIX}.turnLeft`,
  right: `${I18N_PREFIX}.turnRight`,
};

/** Track numbers from left to right as seen from above the field. */
const PASS_POSITIONS: Record<TurnDirection, number[]> = {
  left: [3, 2, 1],
  right: [1, 2, 3],
};

// The diagram is drawn for a right turn and mirrored horizontally for a left turn
const LEFT_TURN_DIAGRAM_TRANSFORM = 'translate(480 0) scale(-1 1)';

const SPACING_12_LABEL = '1 ↔ 2';
const SPACING_23_LABEL = '2 ↔ 3';

@Component({
  selector: 'app-pass-to-pass-calculator',
  standalone: true,
  imports: [FormsModule, AppSharedModule],
  templateUrl: './pass-to-pass-calculator.component.html',
  styleUrl: './pass-to-pass-calculator.component.scss',
})
export class PassToPassCalculatorComponent {
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly storage = inject(PassToPassStorageService);
  private userId: string | null = null;

  draft = createEmptyPassToPassDraft();
  calculation = calculatePassToPass(this.draft);
  storageUnavailable = false;
  edited = false;
  confirming = false;
  readonly offsetDigits = OFFSET_DIGITS;
  readonly initialFields: DraftInputField[] = [
    'lineSpacing',
    'lineCount',
    'lateralOffset',
  ];
  readonly measurementFields: DraftInputField[] = [
    'measuredSpacing12',
    'measuredSpacing23',
  ];
  readonly directions = ['left', 'right'] as const;

  constructor() {
    inject(AuthStateService)
      .user$.pipe(
        map((user) => user?.id || null),
        distinctUntilChanged(),
        takeUntilDestroyed(),
      )
      .subscribe((userId) => {
        this.userId = userId;
        this.draft = this.storage.load(userId) ?? createEmptyPassToPassDraft();
        this.calculation = calculatePassToPass(this.draft);
        this.edited = false;
        this.storageUnavailable = false;
      });
  }

  get canAdvance(): boolean {
    return canAdvanceToNextPass(this.calculation);
  }

  get isFollowUpPass(): boolean {
    return this.draft.passNumber > FIRST_PASS_NUMBER;
  }

  /** Separator the inputs are rewritten with on blur, following the user's language. */
  private get decimalSeparator(): string {
    return getDecimalSeparator(this.translate.currentLang);
  }

  get passPositions(): number[] {
    return PASS_POSITIONS[this.draft.turnDirection];
  }

  get turnLabelKey(): string {
    return TURN_LABEL_KEYS[this.draft.turnDirection];
  }

  get diagramTransform(): string | null {
    if (this.draft.turnDirection === 'left') {
      return LEFT_TURN_DIAGRAM_TRANSFORM;
    }
    return null;
  }

  /** Spacing tags under the diagram, ordered left to right like the tracks. */
  get diagramMeasurements(): DiagramMeasurement[] {
    const spacing12 = {
      label: SPACING_12_LABEL,
      value: this.displayMeasurement(this.draft.measuredSpacing12),
    };
    const spacing23 = {
      label: SPACING_23_LABEL,
      value: this.displayMeasurement(this.draft.measuredSpacing23),
    };
    if (this.draft.turnDirection === 'left') {
      return [spacing23, spacing12];
    }
    return [spacing12, spacing23];
  }

  unitKey(field: DraftInputField): string {
    if (field === 'lineCount') {
      return `${I18N_PREFIX}.linesUnit`;
    }
    return `${I18N_PREFIX}.meterUnit`;
  }

  inputMode(field: DraftInputField): string {
    if (field === 'lineCount') {
      return 'numeric';
    }
    return 'decimal';
  }

  updateDraft(changes: Partial<PassToPassDraft>): void {
    this.setDraft({ ...this.draft, ...changes });
    this.edited = true;
  }

  updateInput(field: DraftInputField, value: string): void {
    // Initial values are locked once a previous pass has applied its corrections
    if (this.isFollowUpPass && this.initialFields.includes(field)) {
      return;
    }
    this.updateDraft({ [field]: value });
  }

  normalizeInput(field: DraftInputField): void {
    this.updateInput(
      field,
      normalizeDraftField(field, this.draft[field], this.decimalSeparator),
    );
  }

  displayNumber(value: number | null, digits = WIDTH_DIGITS): string {
    return formatLocalizedNumber(value, digits, this.translate.currentLang);
  }

  displayMeasurement(value: string): string {
    return this.displayNumber(parseDecimal(value));
  }

  async nextPass(): Promise<void> {
    const next = advanceToNextPass(
      this.draft,
      this.calculation,
      this.decimalSeparator,
    );
    if (next) {
      await this.confirmChange('nextPass', next, next.passNumber);
    }
  }

  async previousPass(): Promise<void> {
    const previous = revertToPreviousPass(this.draft, this.decimalSeparator);
    if (previous !== this.draft) {
      await this.confirmChange('previousPass', previous, previous.passNumber);
    }
  }

  async restart(): Promise<void> {
    await this.confirmChange('restart', createEmptyPassToPassDraft());
  }

  backToUtilities(): void {
    this.router.navigate(['/utilities']);
  }

  private setDraft(draft: PassToPassDraft): void {
    this.draft = draft;
    this.calculation = calculatePassToPass(draft);
    this.storageUnavailable = !this.storage.save(this.userId, draft);
  }

  /**
   * Applies `next` only after the user confirms. The change is dropped if the
   * draft was edited while the dialog was open, so a stale transition never
   * overwrites newer input.
   */
  private async confirmChange(
    action: PassAction,
    next: PassToPassDraft,
    number?: number,
  ): Promise<void> {
    if (this.confirming) {
      return;
    }
    this.confirming = true;
    const current = this.draft;
    try {
      const result = await swal.fire({
        title: this.translate.instant(
          `${I18N_PREFIX}.${action}ConfirmationTitle`,
          { number },
        ),
        text: this.translate.instant(
          `${I18N_PREFIX}.${action}ConfirmationMessage`,
          { number },
        ),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.translate.instant(
          `${I18N_PREFIX}.${CONFIRM_BUTTON_KEYS[action]}`,
          { number },
        ),
        cancelButtonText: this.translate.instant(`${I18N_PREFIX}.cancel`),
      });
      if (result.isConfirmed && current === this.draft) {
        this.setDraft(next);
        this.edited = false;
      }
    } finally {
      this.confirming = false;
    }
  }
}
